/**
 * Хуки данных биллинга/фолио (#338).
 *
 * Гость: GET /v1/me/folio (свои счета + неоплаченный итог, ownership).
 * Персонал/менеджер: GET /v1/folios (все счета), GET /v1/folios/{id} (детали),
 * + мутации: добавить позицию, записать оплату, сменить статус.
 *
 * Money считает БД (триггеры folio_recalc_balance / settlement_apply_to_folio);
 * каждая мутация возвращает пересчитанный фолио, поэтому после успеха просто
 * инвалидируем кэши списка/деталей.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addFolioLineItem,
  getFolioDetail,
  getMyFolio,
  listFolios,
  recordSettlement,
  setFolioStatus,
} from '@hsc/api-client';
import type {
  AddFolioLineInput,
  FolioDetail,
  FolioListResponse,
  MyFolioResponse,
  RecordSettlementInput,
  SetFolioStatusInput,
} from '@hsc/types';

/** Гость: свои счета + общий неоплаченный итог. */
export function useMyFolio() {
  return useQuery<MyFolioResponse>({
    queryKey: ['my-folio'],
    queryFn: getMyFolio,
  });
}

/** Персонал/менеджер: все счета тенанта. */
export function useFolios(limit = 100) {
  return useQuery<FolioListResponse>({
    queryKey: ['folios', limit],
    queryFn: () => listFolios({ limit }),
  });
}

/** Персонал: детали одного фолио с позициями. */
export function useFolioDetail(id: string | undefined) {
  return useQuery<FolioDetail>({
    queryKey: ['folio', id],
    queryFn: () => getFolioDetail(id as string),
    enabled: Boolean(id),
  });
}

function useFolioInvalidation() {
  const qc = useQueryClient();
  return (id: string) => {
    void qc.invalidateQueries({ queryKey: ['folios'] });
    void qc.invalidateQueries({ queryKey: ['folio', id] });
    void qc.invalidateQueries({ queryKey: ['my-folio'] });
  };
}

/** Персонал: добавить позицию в счёт гостя. */
export function useAddFolioLine(folioId: string) {
  const invalidate = useFolioInvalidation();
  return useMutation<FolioDetail, Error, AddFolioLineInput>({
    mutationFn: (input) => addFolioLineItem(folioId, input),
    onSuccess: () => invalidate(folioId),
  });
}

/** Персонал: записать оплату (отметить оплаченным). */
export function useRecordSettlement(folioId: string) {
  const invalidate = useFolioInvalidation();
  return useMutation<FolioDetail, Error, RecordSettlementInput>({
    mutationFn: (input) => recordSettlement(folioId, input),
    onSuccess: () => invalidate(folioId),
  });
}

/** Персонал: сменить статус фолио. */
export function useSetFolioStatus(folioId: string) {
  const invalidate = useFolioInvalidation();
  return useMutation<FolioDetail, Error, SetFolioStatusInput>({
    mutationFn: (input) => setFolioStatus(folioId, input),
    onSuccess: () => invalidate(folioId),
  });
}
